import { IReport } from '@opengov/reporting-types';

import { entityById } from './entity';
import { baseReportsV2ServiceUrl } from './paths';
import { getServiceURL, platform } from './transportWrapper';
import { IDataset, IEntity, ILegacyGridReportSchemaInfo, ILegacyReport, IPackage } from './types';

export const legacyReportFields = [
  'id',
  'uuid',
  'entity_id',
  'entity',
  'coa_id',
  'report_type',
  'report_type_name',
  'access_code',
  'hidden',
  'name',
  'description',
  'properties',
  'coa_mask_id',
  'created',
  'updated',
  'is_default',
  'is_private',
  'position',
  'owner',
  'visibility',
  'is_geo_enabled',
  'year',
  'data_sets',
  'transaction_id'
];

export const legacyReportById = async (
  reportId: string | number,
  clientOptions?: RequestInit
): Promise<ILegacyReport> =>
  platform
    .options(clientOptions, true)
    .url(`/api/v1/reports/${reportId}`)
    .query({ fields: legacyReportFields.join(',') })
    .get()
    .json()
    .then((report: ILegacyReport): ILegacyReport => {
      return report;
    });

export const reportByEntityId = async (
  entityId: string | number,
  limit: number,
  reportFields: string[] = legacyReportFields,
  clientOptions?: RequestInit
): Promise<ILegacyReport[]> =>
  platform
    .options(clientOptions, true)
    .url(`/api/v1/reports`)
    .query({
      entity_id: entityId,
      limit,
      fields: reportFields.join(',')
    })
    .get()
    .json()
    .then(({ reports }: { reports: ILegacyReport[] }): ILegacyReport[] => {
      return reports;
    });

export const deleteLegacyReportById = async (
  reportId: string | number,
  clientOptions?: RequestInit
): Promise<ILegacyReport[]> => platform.options(clientOptions, true).url(`/api/v1/reports/${reportId}`).delete();

export const reportsByCoaId = async (coaId: string | number, clientOptions?: RequestInit): Promise<ILegacyReport[]> =>
  platform
    .options(clientOptions, true)
    .url(`/api/v1/reports`)
    .query({ coa_id: coaId, fields: legacyReportFields.join(',') })
    .get()
    .json()
    .then((response: { reports: ILegacyReport[] }): ILegacyReport[] => {
      return response.reports;
    });

export const schemaForGridReport = async (
  report: ILegacyReport,
  clientOptions?: RequestInit
): Promise<ILegacyGridReportSchemaInfo> =>
  platform
    .options(clientOptions, true)
    .url(`/api/transactions/v2/schema/${report.transaction_id}`)
    .get()
    .json()
    .then((reportSchema: ILegacyGridReportSchemaInfo) => reportSchema);

export const getFiscalPeriod = (targetDataset: IDataset, entity: IEntity) =>
  ((targetDataset.month - entity.fiscal_year_start_month + 12) % 12) + 1;

export const getRelativeDataset = (
  targetDataset: IDataset,
  report: ILegacyReport,
  entity: IEntity,
  offset: number
): IDataset => {
  if (!targetDataset.month && targetDataset.year) {
    return report.data_sets.find(
      (other) =>
        other.year && other.data_set_type === targetDataset.data_set_type && other.year === targetDataset.year + offset
    );
  }
  if (targetDataset.fiscal_period + offset > 14 || targetDataset.fiscal_period + offset < 1) {
    return null;
  }
  //tslint:disable-next-line:prefer-array-literal
  return report.data_sets.find(
    (other) =>
      other.fiscal_period &&
      other.fiscal_year === targetDataset.fiscal_year &&
      other.data_set_type === targetDataset.data_set_type &&
      other.fiscal_period === targetDataset.fiscal_period + offset
  );
};

const isProjection = (dataSet: IDataset) =>
  dataSet.data_set_type === 'projection' || dataSet.data_set_type === 'system_projection';
const isAnnual = (dataSet: IDataset) => !dataSet.month;
const isBudget = (dataSet: IDataset) => dataSet.data_set_type === 'budget';
const isActual = (dataSet: IDataset) => dataSet.data_set_type === 'actual';

const allowVarianceCalculation = (targetDataset: IDataset, dataset: IDataset): boolean =>
  dataset.year === targetDataset.year &&
  (((isActual(dataset) || isProjection(dataset)) && isBudget(targetDataset)) ||
    (isBudget(dataset) &&
      (isActual(targetDataset) || isProjection(targetDataset)) &&
      (isAnnual(dataset) || isAnnual(targetDataset) || dataset.month === targetDataset.month)));

export const getRelatedDataSets = (targetDataset: IDataset, report: ILegacyReport, entity: IEntity) => {
  if (!targetDataset) {
    throw new Error('no targetDataset provided');
  }

  const varianceComparisonDataset = report.data_sets
    .slice(0)
    .reverse()
    .find((dataset) => {
      if (isBudget(targetDataset)) {
        return (
          (allowVarianceCalculation(targetDataset, dataset) &&
            isActual(dataset) &&
            dataset.year === targetDataset.year) ||
          (isProjection(dataset) && dataset.year === targetDataset.year)
        );
      } else {
        return allowVarianceCalculation(targetDataset, dataset) && dataset.order === targetDataset.order - 1;
      }
    });

  const budgetDataset = () => {
    if (isBudget(targetDataset)) {
      return;
    }

    return report.data_sets.find(
      (dataset) => isBudget(dataset) && !dataset.month && dataset.year === targetDataset.year
    );
  };

  return {
    varianceComparisonDataset: varianceComparisonDataset,
    budgetDataset: budgetDataset(),
    previous: getRelativeDataset(targetDataset, report, entity, -1),
    next: getRelativeDataset(targetDataset, report, entity, 1)
  };
};

export const entityForReport = async (report: ILegacyReport, clientOptions?: RequestInit): Promise<IEntity> => {
  return entityById(report.entity_id, clientOptions).then((entity: IEntity): IEntity => {
    return entity;
  });
};

export const getPackageForReport = async (report: ILegacyReport, clientOptions?: RequestInit): Promise<IPackage> =>
  platform
    .options(clientOptions, true)
    .url(`/api/transparency/v1/package/${report.coa_id}`)
    .json({
      coa_id: report.coa_id,
      coa_mask_id: report.coa_mask_id,
      data_sets: report.data_sets.map((r) => r.id)
    })
    .post()
    .json()
    .then((result) => result as IPackage);

export const createReportCopy = async (
  reportId: string,
  title: string,
  description: string,
  copySavedViews: boolean,
  parentFolderId?: string | null,
  reportingServiceHost?: string,
  clientOptions?: RequestInit
): Promise<IReport> =>
  getServiceURL(reportingServiceHost)
    .options(clientOptions, true)
    .url(`${baseReportsV2ServiceUrl}/reports/${reportId}/copy`)
    .json({
      reportId,
      title,
      description,
      parentFolderId: parentFolderId ?? 'none'
    })
    .post()
    .json();
